
import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

export const useProfileData = () => {
  const [username, setUsername] = useState<string>("");
  const [bio, setBio] = useState<string>("");
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [nationality, setNationality] = useState<string>("");
  const [creatorTypes, setCreatorTypes] = useState<string[]>([]);
  const [userRole, setUserRole] = useState<string>("regular");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getProfile = useCallback(async (userId: string) => {
    console.log("Starting getProfile for userId:", userId);
    try {
      setIsLoading(true);
      setError(null);
      
      // First, verify the session
      const { data: { session } } = await supabase.auth.getSession();
      console.log("Session check in getProfile:", { hasSession: !!session });
      
      if (!session) {
        console.error("No active session in getProfile");
        throw new Error('No active session');
      }

      // Then try to get the profile
      console.log("Fetching profile data...");
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      console.log("Profile fetch result:", { 
        hasData: !!data, 
        error: error?.message,
        data: data 
      });

      if (error) {
        console.error("Profile fetch error:", error);
        throw error;
      }

      let profileData = null;

      if (!data) {
        console.log("No profile found, creating default profile");
        // If no profile exists, create one
        const { error: insertError } = await supabase
          .from("profiles")
          .insert([{ 
            id: userId,
            username: "",
            bio: "",
            avatar_url: "",
            nationality: "",
            creator_type: [],
            creator_status: "regular"
          }])
          .single();

        if (insertError) {
          console.error("Error creating profile:", insertError);
          throw insertError;
        }

        console.log("Created default profile, fetching new profile");
        // Fetch the newly created profile
        const { data: newProfile, error: fetchError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", userId)
          .maybeSingle();

        if (fetchError) {
          console.error("Error fetching new profile:", fetchError);
          throw fetchError;
        }

        console.log("New profile fetched:", newProfile);
        profileData = newProfile;
      } else {
        profileData = data;
      }

      // Update state with profile data
      console.log("Updating state with profile data:", profileData);
      setUsername(profileData?.username || "");
      setBio(profileData?.bio || "");
      setAvatarUrl(profileData?.avatar_url || "");
      setNationality(profileData?.nationality || "");
      setCreatorTypes(Array.isArray(profileData?.creator_type) 
        ? profileData.creator_type.map(type => String(type))
        : []);
      setUserRole(profileData?.creator_status || "regular");

      return true;
    } catch (error) {
      console.error("Error in getProfile:", error);
      setError("Failed to load user profile");
      toast.error("Error loading profile");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array since this function doesn't depend on any props or state

  return {
    username,
    setUsername,
    bio,
    setBio,
    avatarUrl,
    setAvatarUrl,
    nationality,
    setNationality,
    creatorTypes,
    setCreatorTypes,
    userRole,
    setUserRole,
    getProfile,
    isLoading,
    error
  };
};
